
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

export default function ServersStoragePage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [compareProducts, setCompareProducts] = useState<number[]>([]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(45000);

  const handleCompareToggle = (productId: number) => {
    if (compareProducts.includes(productId)) {
      setCompareProducts(compareProducts.filter(id => id !== productId));
    } else if (compareProducts.length < 4) {
      setCompareProducts([...compareProducts, productId]);
    }
  };

  const clearComparison = () => {
    setCompareProducts([]);
  };

  const productNames: Record<number, string> = {
    1: 'Dell PowerEdge R750',
    2: 'HP ProLiant DL380 Gen11',
    3: 'NetApp FAS8700',
    4: 'Synology RS3621RPxs',
    5: 'IBM FlashSystem 7300',
    6: 'Cisco UCS 5108 Chassis',
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-primary flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary">Hardware</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">{isNL ? 'Servers & Opslag' : 'Servers & Storage'}</span>
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start">
            <div className="max-w-4xl">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{isNL ? 'Servers & Opslagoplossingen' : 'Servers & Storage Solutions'}</h1>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Enterprise-grade servers, opslagarrays, NAS-systemen en SAN-oplossingen voor de infrastructuurbehoeften van uw bedrijf.'
                  : 'Enterprise-grade servers, storage arrays, NAS systems, and SAN solutions for your business infrastructure needs.'}
              </p>

              {/* Brand Sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{isNL ? 'Vertrouwde Enterprise Merken' : 'Trusted Enterprise Brands'}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-server-fill text-[#007DB8] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Dell EMC</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-server-fill text-[#0096D6] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">HP Enterprise</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-server-fill text-[#1F70C1] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">IBM</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-server-fill text-[#1BA0D7] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Cisco</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{isNL ? 'Opslagspecialisten' : 'Storage Specialists'}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-hard-drive-2-fill text-[#0067C5] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">NetApp</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-database-2-fill text-[#ED1C24] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Pure Storage</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-server-fill text-[#00B140] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Supermicro</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-hard-drive-fill text-[#F16625] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Synology</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Solutions Lists */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{isNL ? 'Enterprise Serveroplossingen' : 'Enterprise Server Solutions'}</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Rack-, Tower- & Blade-servers' : 'Rack, Tower & Blade Servers'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'High-Performance Computing (HPC) Oplossingen' : 'High-Performance Computing (HPC) Solutions'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Hypergeconvergeerde Infrastructuur' : 'Hyperconverged Infrastructure'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Bedrijfskritische Servers' : 'Mission-Critical Servers'}
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{isNL ? 'Opslaginfrastructuur' : 'Storage Infrastructure'}</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'All-Flash & Hybride Opslagarrays' : 'All-Flash & Hybrid Storage Arrays'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Network Attached Storage (NAS)' : 'Network Attached Storage (NAS)'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Storage Area Networks (SAN)' : 'Storage Area Networks (SAN)'}
                    </li>
                    <li className="flex items-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {isNL ? 'Software-Defined Opslagoplossingen' : 'Software-Defined Storage Solutions'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button className="flex items-center text-gray-600 hover:text-primary cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-scales-3-line"></i>
                </div>
                {isNL ? 'Vergelijken' : 'Compare'} (<span>{compareProducts.length}</span>)
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                <button className="p-2 text-primary bg-blue-50 rounded cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </div>
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-list-check"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-28">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                <button className="text-primary text-sm hover:underline cursor-pointer">{isNL ? 'Alles Wissen' : 'Clear All'}</button>
              </div>

              {/* Product Type */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Producttype' : 'Product Type'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Enterprise Servers' : 'Enterprise Servers'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Opslagarrays' : 'Storage Arrays'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'NAS-systemen' : 'NAS Systems'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'SAN-oplossingen' : 'SAN Solutions'}</span>
                  </label>
                </div>
              </div>

              {/* Brand */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Merk' : 'Brand'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">Dell Technologies</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">HP Enterprise</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">IBM</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">Cisco</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">NetApp</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">Supermicro</span>
                  </label>
                </div>
              </div>

              {/* Storage Capacity */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Opslagcapaciteit' : 'Storage Capacity'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">1TB - 10TB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">10TB - 50TB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">50TB - 100TB</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">100TB+</span>
                  </label>
                </div>
              </div>

              {/* Performance Tier */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Prestatieniveau' : 'Performance Tier'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Instapniveau' : 'Entry Level'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Middensegment' : 'Mid-Range'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'High-End' : 'High-End'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Enterprise Klasse' : 'Enterprise Class'}</span>
                  </label>
                </div>
              </div>

              {/* Availability */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Beschikbaarheid' : 'Availability'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Voorbestelling' : 'Pre-Order'}</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Aangepaste Configuratie' : 'Custom Configuration'}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {isNL ? '24 van 156 producten weergegeven' : 'Showing 24 of 156 products'}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary">
                    <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                    <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Toon:' : 'Show:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary">
                    <option value="12">12</option>
                    <option value="24" selected>24</option>
                    <option value="48">48</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {/* Product 1 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative">
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/b2dd4bfac7fab0891bca43686f4b5b7d.jpg" 
                      alt="Dell PowerEdge R750"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <input
                      type="checkbox"
                      className="w-5 h-5 cursor-pointer"
                      checked={compareProducts.includes(1)}
                      onChange={() => handleCompareToggle(1)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-primary bg-blue-50 px-2 py-1 rounded-full">{isNL ? 'Enterprise Server' : 'Enterprise Server'}</span>
                    <div className="text-yellow-400 flex">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-half-fill"></i></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Dell PowerEdge R750</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {isNL
                      ? '2U rackserver met dubbele Intel Xeon Scalable-processors, tot 32 DIMM-slots en flexibele opslagopties.'
                      : '2U rack server with dual Intel Xeon Scalable processors, up to 32 DIMM slots, and flexible storage options.'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-medium">{isNL ? 'Dubbele Intel Xeon Gold' : 'Dual Intel Xeon Gold'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Geheugen:' : 'Memory:'}</span>
                      <span className="font-medium">{isNL ? '64GB DDR4 (tot 4TB)' : '64GB DDR4 (up to 4TB)'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Opslag:' : 'Storage:'}</span>
                      <span className="font-medium">8x 2.5&quot; SAS/SATA</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                      {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Product 2 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative">
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src="https://readdy.ai/api/search-image?query=HP%20ProLiant%20DL380%20Gen11%20enterprise%20rack%20server%20with%20dual%20Intel%20Xeon%20processors%20silver%20chassis%20front%20view%20with%20green%20LED%20indicators%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=hp-dl380-gen11&orientation=landscape" 
                      alt="HP ProLiant DL380 Gen11"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <input
                      type="checkbox"
                      className="w-5 h-5 cursor-pointer"
                      checked={compareProducts.includes(2)}
                      onChange={() => handleCompareToggle(2)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-primary bg-blue-50 px-2 py-1 rounded-full">{isNL ? 'Enterprise Server' : 'Enterprise Server'}</span>
                    <div className="text-yellow-400 flex">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">HP ProLiant DL380 Gen11</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {isNL
                      ? '2U rackserver ontworpen voor virtualisatie- en cloudworkloads met geavanceerde beveiligingsfuncties.'
                      : '2U rack server designed for virtualization and cloud workloads with advanced security features.'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-medium">{isNL ? 'Dubbele Intel Xeon Platinum' : 'Dual Intel Xeon Platinum'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Geheugen:' : 'Memory:'}</span>
                      <span className="font-medium">{isNL ? '128GB DDR5 (tot 8TB)' : '128GB DDR5 (up to 8TB)'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Opslag:' : 'Storage:'}</span>
                      <span className="font-medium">12x 3.5&quot; SAS/SATA</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                      {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Product 3 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative">
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src="https://readdy.ai/api/search-image?query=NetApp%20FAS8700%20enterprise%20storage%20array%20system%20with%20multiple%20drive%20bays%20blue%20and%20white%20chassis%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=netapp-fas8700&orientation=landscape" 
                      alt="NetApp FAS8700"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Voorbestelling' : 'Pre-Order'}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <input
                      type="checkbox"
                      className="w-5 h-5 cursor-pointer"
                      checked={compareProducts.includes(3)}
                      onChange={() => handleCompareToggle(3)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">{isNL ? 'Opslagarray' : 'Storage Array'}</span>
                    <div className="text-yellow-400 flex">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-line"></i></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">NetApp FAS8700</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {isNL
                      ? 'Hoogwaardig unified opslagsysteem met geavanceerd gegevensbeheer en cloudintegratie.'
                      : 'High-performance unified storage system with advanced data management and cloud integration.'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Capaciteit:' : 'Capacity:'}</span>
                      <span className="font-medium">{isNL ? 'Tot 2,8PB' : 'Up to 2.8PB'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Prestaties:' : 'Performance:'}</span>
                      <span className="font-medium">1,2M IOPS</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Protocollen:' : 'Protocols:'}</span>
                      <span className="font-medium">NFS, SMB, iSCSI, FC</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                      {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Product 4 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative">
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Synology%20RS3621RPxs%20rackmount%20NAS%20system%20with%2012%20drive%20bays%20black%20chassis%20front%20view%20with%20orange%20accents%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=synology-rs3621&orientation=landscape" 
                      alt="Synology RS3621RPxs"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <input
                      type="checkbox"
                      className="w-5 h-5 cursor-pointer"
                      checked={compareProducts.includes(4)}
                      onChange={() => handleCompareToggle(4)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">{isNL ? 'NAS-systeem' : 'NAS System'}</span>
                    <div className="text-yellow-400 flex">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-half-fill"></i></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Synology RS3621RPxs</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {isNL
                      ? '12-bay rackmount NAS met krachtige AMD Ryzen-processor en schaalbare opslag.'
                      : '12-bay rackmount NAS with high-performance AMD Ryzen processor and scalable storage.'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-medium">AMD Ryzen V1780B</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Geheugen:' : 'Memory:'}</span>
                      <span className="font-medium">32GB DDR4 ECC</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Bays:' : 'Bays:'}</span>
                      <span className="font-medium">12x 3.5&quot; SATA/SAS</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                      {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Product 5 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative">
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src="https://readdy.ai/api/search-image?query=IBM%20FlashSystem%207300%20all-flash%20storage%20array%20with%20sleek%20black%20chassis%20and%20blue%20IBM%20branding%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=ibm-flashsystem-7300&orientation=landscape" 
                      alt="IBM FlashSystem 7300"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Aangepaste Configuratie' : 'Custom Config'}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <input
                      type="checkbox"
                      className="w-5 h-5 cursor-pointer"
                      checked={compareProducts.includes(5)}
                      onChange={() => handleCompareToggle(5)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">{isNL ? 'Opslagarray' : 'Storage Array'}</span>
                    <div className="text-yellow-400 flex">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">IBM FlashSystem 7300</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {isNL
                      ? 'All-flash opslagarray met AI-gestuurde optimalisatie en enterprise-klasse betrouwbaarheid.'
                      : 'All-flash storage array with AI-powered optimization and enterprise-class reliability.'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Capaciteit:' : 'Capacity:'}</span>
                      <span className="font-medium">{isNL ? 'Tot 4PB' : 'Up to 4PB'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Prestaties:' : 'Performance:'}</span>
                      <span className="font-medium">2,2M IOPS</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Latentie:' : 'Latency:'}</span>
                      <span className="font-medium">70µs</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                      {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Product 6 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative">
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Cisco%20UCS%205108%20blade%20server%20chassis%20with%208%20blade%20slots%20silver%20and%20black%20chassis%20front%20view%20with%20blue%20Cisco%20branding%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=cisco-ucs-5108&orientation=landscape" 
                      alt="Cisco UCS 5108 Chassis"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <input
                      type="checkbox"
                      className="w-5 h-5 cursor-pointer"
                      checked={compareProducts.includes(6)}
                      onChange={() => handleCompareToggle(6)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-primary bg-blue-50 px-2 py-1 rounded-full">{isNL ? 'Blade Server' : 'Blade Server'}</span>
                    <div className="text-yellow-400 flex">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill"></i></div>
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-line"></i></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Cisco UCS 5108 Chassis</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {isNL
                      ? '6RU blade server chassis met ondersteuning voor maximaal 8 halve-breedte of 4 volle-breedte blade servers.'
                      : '6RU blade server chassis supporting up to 8 half-width or 4 full-width blade servers.'}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Slots:' : 'Slots:'}</span>
                      <span className="font-medium">{isNL ? '8x Halve-Breedte Blades' : '8x Half-Width Blades'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Voeding:' : 'Power:'}</span>
                      <span className="font-medium">4x 2500W PSU</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{isNL ? 'Netwerk:' : 'Fabric:'}</span>
                      <span className="font-medium">{isNL ? 'Unified Fabric' : 'Unified Fabric'}</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                      {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-12">
              <div className="text-gray-600">
                {isNL ? '1-24 van 156 resultaten weergegeven' : 'Showing 1-24 of 156 results'}
              </div>
              <div className="flex items-center space-x-2">
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer" disabled>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                </button>
                <button className="px-3 py-2 bg-primary text-white rounded cursor-pointer">1</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">2</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">3</button>
                <span className="px-2 text-gray-500">...</span>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">7</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transition-transform duration-300 z-40 ${
          compareProducts.length > 0 ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="font-medium">
                {isNL ? 'Producten Vergelijken' : 'Compare Products'} ({compareProducts.length}/4)
              </span>
              <div className="flex space-x-2">
                {compareProducts.map((id) => (
                  <div key={id} className="bg-gray-100 px-3 py-1 rounded text-sm">
                    {productNames[id] || `Product ${id}`}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                className="text-gray-600 hover:text-gray-800 cursor-pointer"
                onClick={clearComparison}
              >
                {isNL ? 'Alles Wissen' : 'Clear All'}
              </button>
              <button className="bg-primary text-white px-6 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                {isNL ? 'Selectie Vergelijken' : 'Compare Selected'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
